import {LobbyImage} from '@colonist/types'
import {createDivIn, createImgIn, createParagraphIn, setElementOnClick} from '../../ui_utils/DomUtils.ts'
import {type TranslationObject} from '@colonist/i18n'

export interface ItemData {
    name: TranslationObject
    image: LobbyImage
    available: boolean
    selected: boolean
    canToggle?: boolean
    bannerText?: TranslationObject | undefined
    clickAction: () => void
    infoAction?: () => void
    showInfoIcon?: boolean
}

export class UIComponentItemScrollCell {
    readonly container: HTMLDivElement

    private readonly name: TranslationObject
    private readonly available: boolean
    private readonly selected: boolean
    private readonly canToggle: boolean
    private readonly bannerText: TranslationObject | undefined
    private readonly clickAction: () => void
    private readonly infoAction: (() => void) | undefined

    constructor(container: HTMLDivElement, data: ItemData) {
        this.container = createDivIn(container, 'item-scroll-cell')
        this.name = data.name

        const image = createImgIn(this.container, data.image, 'item-scroll-cell-image')
        const text = createParagraphIn(this.container, this.name, 'item-scroll-cell-text')
        image.draggable = false
        if(text.textContent) text.textContent = text.textContent.replace(/-/g, '\u2011')
        if(data.showInfoIcon) createImgIn(this.container, LobbyImage.InfoIcon, 'item-scroll-cell-info-icon')

        this.available = data.available
        this.selected = data.selected
        this.canToggle = data.canToggle ?? false
        this.bannerText = data.bannerText ?? undefined
        this.clickAction = data.clickAction
        this.infoAction = data.infoAction
        this.init()
    }

    updateInteractability(isGuest: boolean) {
        this.setView(isGuest)

        if(this.shouldDisableOnClick(isGuest)) {
            setElementOnClick(this.container, () => undefined)
            return
        }

        this.shouldShowInfoOnClick(isGuest) ? this.setInfoOnClick() : this.setOnClick()
    }

    private init() {
        this.setOnClick()
        this.updateCellView()
    }

    private updateCellView() {
        if(!this.available) this.setUnavailable()
        if(this.selected) this.setSelected()
        if(this.bannerText) this.createBanner(this.bannerText)
    }

    private createBanner(bannerText: TranslationObject) {
        const banner = createDivIn(this.container, 'badge-green badge-sm')
        createParagraphIn(banner, bannerText, '')
    }

    private setView(isGuest: boolean) {
        this.container.classList.toggle('guest-view', isGuest)
    }

    private setUnavailable() {
        this.container.classList.add('unavailable')
        createImgIn(this.container, LobbyImage.Lock, 'item-scroll-cell-lock')
        if(this.canToggle) this.setInfoOnClick()
    }

    private setSelected() {
        this.container.classList.add('selected')
    }

    private shouldDisableOnClick(isGuest: boolean) {
        if(isGuest && this.canToggle) return true
        return false
    }

    private shouldShowInfoOnClick(isGuest: boolean) {
        if(isGuest) return true
        if(this.canToggle && this.available) return false
        if(this.canToggle && !this.available) return true
        if(this.selected) return true
        return false
    }

    private setOnClick() {
        setElementOnClick(this.container, this.clickAction)
    }

    private setInfoOnClick() {
        if(this.infoAction == undefined) {
            setElementOnClick(this.container, () => undefined)
            return
        }
        setElementOnClick(this.container, this.infoAction)
    }
}
